import { AlertTriangle } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtArr } from "../format";

export interface TotalArrData {
  /** Current period total billed ARR. */
  current: number | null;
  /** Most recent complete-month ARR (e.g. "$6.40M"). */
  lastCompleteMonth: { label: string; value: number | null };
  /** Booked-not-billed pipeline: signed master contracts not yet billing. */
  bookedNotBilled: number | null;
  /** Accounts behind the booked-not-billed number. */
  bookedNotBilledAccounts?: number;
  /** Billed + booked-not-billed headline for the corner stat. */
  billedPlusBooked: number | null;
  /** All billed revenue × 12, anchored to the last complete month. */
  annualizedRevenue: number | null;
  /** Month label for annualizedRevenue (e.g. "May 2026"). */
  annualizedRevenueLabel?: string;
  /** Last-3-complete-months avg revenue (ex pass-through) × 12. */
  annualizedRevenue3moAvg: number | null;
  /** Calendar-YTD actual billed revenue through the anchor month. */
  ytdRevenue: number | null;
  /** Label for the YTD window (e.g. "Jan–May 2026"). */
  ytdRevenueLabel?: string;
  /** Net new ARR for the period (new + PLG + PO + replacement Δ − churn − contraction). */
  netRevenue: number | null;
  /** Optional caveat when bookedNotBilled is null (e.g. pipeline data stale). */
  bookedNotBilledNote?: string;
  /** Free-text source line shown at the bottom. */
  source?: string;
}

export function TotalArrCard({
  periodLabel,
  data,
  onBookedClick,
}: {
  periodLabel: string;
  data: TotalArrData | null;
  /** When set, the Booked-Not-Billed stat becomes a drill-down trigger. */
  onBookedClick?: () => void;
}) {
  return (
    <Card className="relative col-span-2 overflow-hidden border-transparent bg-gradient-to-br from-power to-power-deep p-6 text-white shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">
            Total Billed ARR
          </div>
          <div className="mt-2 text-4xl font-extrabold tracking-tight tabular-nums">
            {data ? fmtArr(data.current) : "—"}
          </div>
        </div>
        <div className="flex-shrink-0 rounded-lg bg-white/10 px-3.5 py-2.5 text-right">
          <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">
            Billed + Booked Not Billed
          </div>
          <div className="mt-1 text-2xl font-extrabold tracking-tight tabular-nums">
            {data?.billedPlusBooked != null ? fmtArr(data.billedPlusBooked) : "—"}
          </div>
        </div>
      </div>

      <div className="mt-4 flex gap-6 border-t border-white/20 pt-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/65">
            {data?.lastCompleteMonth.label ?? "Last complete month"}
          </div>
          <div className="mt-0.5 text-lg font-extrabold tabular-nums">
            {data ? fmtArr(data.lastCompleteMonth.value) : "—"}
          </div>
        </div>
        <button
          type="button"
          onClick={onBookedClick}
          disabled={!onBookedClick || data?.bookedNotBilled == null}
          className={cn(
            "-m-1 rounded-md p-1 text-left transition",
            onBookedClick && data?.bookedNotBilled != null
              ? "cursor-pointer hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              : "cursor-default",
          )}
          aria-label="Open Booked Not Billed drill-down"
        >
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/65">
            Booked Not Billed
          </div>
          <div className="mt-0.5 text-lg font-extrabold tabular-nums">
            {data?.bookedNotBilled != null ? fmtArr(data.bookedNotBilled) : "—"}
          </div>
          {data?.bookedNotBilled != null && data.bookedNotBilledAccounts != null && (
            <div className="mt-0.5 text-[9px] text-white/50">
              {data.bookedNotBilledAccounts} accounts · signed ≤ 12 mo, not yet billing
            </div>
          )}
          {data?.bookedNotBilled == null && data?.bookedNotBilledNote && (
            <div className="mt-0.5 inline-flex items-center gap-1 text-[9px] text-white/50">
              <AlertTriangle className="h-3 w-3" />
              {data.bookedNotBilledNote}
            </div>
          )}
        </button>
        <div className="ml-auto text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/65">
            Net Revenue · {periodLabel}
          </div>
          <div className="mt-0.5 text-lg font-extrabold tabular-nums">
            {data?.netRevenue != null
              ? `${data.netRevenue >= 0 ? "+" : "−"}${fmtArr(Math.abs(data.netRevenue), 0)}`
              : "—"}
          </div>
          <div className="mt-0.5 text-[9px] text-white/50">
            new + PLG + PO + repl Δ − churn − contraction
          </div>
        </div>
      </div>

      <div className="mt-4 flex gap-8 border-t border-white/20 pt-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/55">
            Annualized Revenue
            {data?.annualizedRevenueLabel ? ` · ${data.annualizedRevenueLabel}` : ""}
          </div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-white/90">
            {data?.annualizedRevenue != null ? fmtArr(data.annualizedRevenue) : "—"}
          </div>
          <div className="mt-0.5 text-[9px] text-white/45">total billed revenue × 12</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/55">
            3-Month Avg Annualized
          </div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-white/90">
            {data?.annualizedRevenue3moAvg != null ? fmtArr(data.annualizedRevenue3moAvg) : "—"}
          </div>
          <div className="mt-0.5 text-[9px] text-white/45">last 3 mo avg × 12 · ex pass-through</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/55">
            YTD Revenue
            {data?.ytdRevenueLabel ? ` · ${data.ytdRevenueLabel}` : ""}
          </div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-white/90">
            {data?.ytdRevenue != null ? fmtArr(data.ytdRevenue) : "—"}
          </div>
          <div className="mt-0.5 text-[9px] text-white/45">actual billed revenue, year to date</div>
        </div>
      </div>

      <div className="mt-3 text-[9px] tracking-wide text-white/40">
        {data?.source ?? "Source TBD"}
      </div>
    </Card>
  );
}
